<template>
  <section class="planning-day">
    <header class="planning-day__header">
      <span class="planning-day__title u-text-md">
        <i class="fas fa-calendar-day"></i> {{ date }}
      </span>
      <button
        v-if="$can('orders.documents') && documentsEnabled"
        type="button"
        class="planning-day__generate-btn"
        :disabled="generating"
        title="Сформировать маршрутные и путевые листы в 1С для этого дня"
        @click.stop="$emit('generate-docs')"
      >
        <i :class="generating ? 'fas fa-spinner fa-spin' : 'fas fa-file-export'"></i>
      </button>
    </header>

    <div class="planning-day__body">
      <div
        v-for="(row, index) in rows"
        :key="`${date}-${index}`"
        class="plan-row"
        :class="{ 'plan-row--active': row.active, 'plan-row--repair': row.inRepair }"
        :title="row.inRepair ? 'Машина на ремонте' : null"
        @click="$emit('set-active', index)"
      >
        <!-- Точка загрузки -->
        <div class="plan-row__cell">
          <button
            type="button"
            class="plan-cell"
            :class="{ 'plan-cell--placeholder': !pointName(row.points[0]) }"
            :data-point="pointKey(pointName(row.points[0]))"
            @click.stop="$emit('edit', { type: 'pointA', row: index })"
          >
            <span class="plan-cell__text">{{ pointName(row.points[0]) || (row.inRepair ? '' : 'Укажите 1 партию') }}</span>
            <i v-if="pointFlag(row.points[0], 'auto')" class="fas fa-robot plan-cell__robot" :title="autoTitle(row.points[0])"></i>
            <span v-if="pointFlag(row.points[0], 'hasWaybill') || pointFlag(row.points[0], 'hasTravelSheet')" class="plan-cell__docs">
              <i v-if="pointFlag(row.points[0], 'hasWaybill')" class="fas fa-route" title="Маршрутный лист сформирован"></i>
              <i v-if="pointFlag(row.points[0], 'hasTravelSheet')" class="fas fa-clipboard-list" title="Путевой лист сформирован"></i>
            </span>
          </button>
        </div>

        <!-- Точка выгрузки -->
        <div class="plan-row__cell">
          <button
            type="button"
            class="plan-cell"
            :class="{ 'plan-cell--placeholder': !pointName(row.points[1]) }"
            :data-point="pointKey(pointName(row.points[1]))"
            @click.stop="$emit('edit', { type: 'pointB', row: index })"
          >
            <span class="plan-cell__text">{{ pointName(row.points[1]) || (row.inRepair ? '' : 'Укажите 2 партию') }}</span>
            <i v-if="pointFlag(row.points[1], 'auto')" class="fas fa-robot plan-cell__robot" :title="autoTitle(row.points[1])"></i>
            <span v-if="pointFlag(row.points[1], 'hasWaybill') || pointFlag(row.points[1], 'hasTravelSheet')" class="plan-cell__docs">
              <i v-if="pointFlag(row.points[1], 'hasWaybill')" class="fas fa-route" title="Маршрутный лист сформирован"></i>
              <i v-if="pointFlag(row.points[1], 'hasTravelSheet')" class="fas fa-clipboard-list" title="Путевой лист сформирован"></i>
            </span>
          </button>
        </div>

        <!-- Авто + водитель -->
        <div class="plan-row__cell plan-row__cell--who">
          <div class="plan-who">
            <!-- Машина в строке уже назначена: выбрать другую нельзя -->
            <span class="plan-chip plan-chip--car plan-chip--static">
              <i class="fas fa-car"></i>{{ carNum(row.driver.car) || '—' }}
            </span>
            <button
              type="button"
              class="plan-chip plan-chip--driver"
              :class="{ 'plan-cell--placeholder': !row.driver.name }"
              @click.stop="$emit('edit', { type: 'executor', row: index })"
            >
              <i v-if="row.driver.name || !row.inRepair" class="fas fa-user"></i>{{ row.driver.name ? shortenName(row.driver.name) : (row.inRepair ? '' : 'Выберите водителя') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { getUser } from '@/auth'

export default {
  name: 'PlanningDay',
  props: {
    date: { type: String, default: '' },
    rows: { type: Array, default: () => [] },
    generating: { type: Boolean, default: false }
  },
  computed: {
    // Пока сервер не создаёт документы в 1С по разнарядке (ONEC_CREATE_ORDER_DOCUMENTS), кнопка скрыта
    documentsEnabled() {
      const user = getUser()
      return !(user && user.features && user.features.order_documents === false)
    }
  },
  methods: {
    // points[k] — объект { name, hasWaybill, hasTravelSheet } для уже
    // назначенных партий (см. OrdersController::actionGet) либо строка/
    // объект без этих полей (партия только что выбрана, документов ещё нет).
    pointFlag(point, key) {
      return !!(point && typeof point === 'object' && point[key])
    },
    // Партию записала система по маршруту машины (погрузка → выгрузка); человек может её поменять, и тогда робот пропадает
    autoTitle(point) {
      const note = point && typeof point === 'object' && point.autoNote ? ' ' + point.autoNote + '.' : ''
      return 'Заполнено автоматически по маршруту машины.' + note + ' Можно изменить вручную: изменённое система не трогает'
    },
    shortenName(fullName) {
      if (!fullName) return ''
      const parts = fullName.trim().split(/\s+/)
      if (parts.length < 2) return parts[0] || ''
      const initials = parts.slice(1).map(p => p.charAt(0).toUpperCase() + '.').join('')
      return `${parts[0]} ${initials}`
    },
    carNum(str) {
      return str
    },
    // points[k] — либо строка (легаси/название маршрута), либо объект
    // { waitlistId, name } — партия, выбранная из очереди waitList.
    pointName(point) {
      if (!point) return ''
      return typeof point === 'object' ? (point.name || '') : point
    },
    pointKey(str) {
      if (!str) return ''
      if (str.includes('ТГОК')) return 'тгок'
      if (str.includes('ЧЦЗ')) return 'чцз'
      if (str.includes('ВОЛК')) return 'волк'
      return ''
    }
  }
}
</script>

<style scoped>
.planning-day {
  background: var(--bg-surface);
  border-radius: var(--radius-md);
  padding: var(--space-1) var(--space-2);
  border: var(--border-width-default) var(--border-style-base) var(--border-default);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  min-height: 0;
  height: 100%;
}

.planning-day__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: var(--space-1);
  border-bottom: var(--border-width-default) var(--border-style-base) var(--bg-subtle);
  margin-bottom: var(--space-1);
  flex-shrink: 0;
}

.planning-day__title {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}
.planning-day__title i {
  color: var(--primary);
  font-size: var(--text-2xs);
}

.planning-day__generate-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: var(--border-width-default) var(--border-style-base) var(--border-default);
  border-radius: var(--radius-xs);
  background: var(--bg-surface);
  color: var(--primary);
  font-size: var(--text-2xs);
  cursor: pointer;
  transition: background var(--duration-fast) var(--ease-base);
}
.planning-day__generate-btn:hover:not(:disabled) { background: var(--bg-selected); }
.planning-day__generate-btn:disabled { cursor: default; opacity: 0.6; }

.planning-day__body { display: flex; flex-direction: column; }

.plan-row {
  display: grid;
  grid-template-columns: var(--plan-col-where) var(--plan-col-where) var(--plan-col-who);
  gap: var(--space-1);
  background: var(--bg-surface-muted);
  border-radius: var(--radius-xs);
  padding: var(--space-0-5) var(--space-1);
  margin-bottom: var(--space-0-5);
  border: var(--border-width-default) var(--border-style-base) var(--color-neutral-200);
  align-items: center;
  flex-shrink: 0;
  min-height: 16px;
  font-size: var(--text-2xs);
  cursor: pointer;
  transition: background var(--duration-fast) var(--ease-base),
              border-color var(--duration-fast) var(--ease-base);
}
.plan-row:hover {
  background: var(--hover-bg);
}
.plan-row--active {
  background: var(--bg-selected);
  border-color: var(--border-selected);
}
.plan-row--repair {
  /* --warning-soft слишком бледный на фоне строки — берём насыщеннее,
     чтобы заливка была явно видна, а не только рамка. */
  background: rgba(245, 158, 11, 0.22);
  border-color: var(--warning);
}
.plan-row--repair:hover { background: rgba(245, 158, 11, 0.3); }
.plan-row:last-child { margin-bottom: 0; }

.plan-row__cell {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  overflow: hidden;
}

.plan-cell {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-0-5) var(--space-1);
  border-radius: var(--radius-xs);
  transition: background var(--duration-fast) var(--ease-base);
  font-size: var(--text-xs);
  font-family: inherit;
  border: none;
  background: transparent;
  text-align: left;
  color: var(--text-primary);
  width: 100%;
}
.plan-cell:hover { background: var(--bg-selected); }
.plan-cell--placeholder { color: var(--text-muted); font-style: italic; }

.plan-cell__text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.plan-cell__robot {
  flex-shrink: 0;
  margin-left: var(--space-3);
  color: var(--primary);
  font-size: 0.9em;
}
.plan-cell__docs {
  flex-shrink: 0;
  display: inline-flex;
  gap: var(--space-2);
  font-size: var(--text-2xs);
  color: var(--success);
}

/* Цветовая метка точки погрузки по названию (как в исходном day.vue) */
.plan-cell[data-point="тгок"] { background: var(--color-point-тгок); color: var(--text-on-dark); }
.plan-cell[data-point="чцз"]  { background: var(--color-point-чцз); color: var(--text-primary); }
.plan-cell[data-point="волк"] { background: var(--color-point-волк); color: var(--text-on-dark); }

.plan-who {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  min-width: 0;
}

.plan-chip {
  display: inline-flex;
  align-items: center;
  gap: var(--space-0-5);
  border: none;
  cursor: pointer;
  padding: var(--space-0-5);
  border-radius: var(--radius-xs);
  font-family: inherit;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
.plan-chip i { font-size: var(--text-2xs); }

.plan-chip--car {
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-brand-700);
  background: var(--primary-soft-2);
  padding-left: var(--space-2);
  padding-right: var(--space-2);
  flex-shrink: 0;
}
.plan-chip--car i { color: var(--primary); }
.plan-chip--static { cursor: default; }

.plan-chip--driver {
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
  padding-left: var(--space-1);
  padding-right: var(--space-1);
  flex: 1;
}
.plan-chip--driver i { color: var(--text-secondary); }
.plan-chip--driver:hover { background: var(--bg-selected); }
</style>
